import React, { useRef, useState, useCallback, useEffect } from "react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import { api } from "../lib/api";
import { Mic, MicOff, Loader2, Volume2, Radio } from "lucide-react";
import { toast } from "sonner";

/**
 * ElevenLabs conversational agent — live mic-in, voice-out.
 * Wraps its inner UI in a ConversationProvider (required by @elevenlabs/react v1.x).
 */
export default function VoiceMode({ compact = false }) {
  return (
    <ConversationProvider>
      <VoiceModeInner compact={compact} />
    </ConversationProvider>
  );
}

function VoiceModeInner({ compact }) {
  const conversation = useConversation({
    onConnect: () => {},
    onDisconnect: () => {},
    onError: (err) => {
      const message = err?.message || String(err);
      toast.error(message);
    },
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const startedAt = useRef(null);

  const status = conversation.status; // "disconnected" | "connecting" | "connected"
  const mode = conversation.mode;     // "speaking" | "listening"
  const active = status === "connected";
  const connecting = status === "connecting" || busy;

  const reportUsage = useCallback(async () => {
    if (!startedAt.current) return;
    const seconds = Math.max(0, Math.round((Date.now() - startedAt.current) / 1000));
    startedAt.current = null;
    if (seconds < 1) return;
    try { await api.post("/assistant/voice/usage", { seconds }); } catch {}
  }, []);

  useEffect(() => {
    // If the session dropped from connected → disconnected, log the duration
    if (status === "disconnected" && startedAt.current) reportUsage();
  }, [status, reportUsage]);

  useEffect(() => {
    const handler = () => { if (startedAt.current) reportUsage(); };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [reportUsage]);

  const start = async () => {
    setError(null);
    setBusy(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const { data } = await api.get("/assistant/voice/signed-url");
      setRemaining(data.remaining_seconds || null);
      startedAt.current = Date.now();
      // Dynamic variables map 1:1 to the {{user_id}} / {{user_name}}
      // placeholders in the agent's system prompt + first_message.
      const dynamicVariables = {
        user_id:   data.user_id || "",
        user_name: data.first_name || data.user_name || "founder",
      };
      await conversation.startSession({
        signedUrl: data.signed_url,
        dynamicVariables,
      });
    } catch (err) {
      startedAt.current = null;
      const msg = err?.response?.data?.detail || err?.message || "Could not start voice mode";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    try { await conversation.endSession(); } catch {}
  };

  const statusLabel =
    status === "connecting" ? "Connecting…"
    : !active ? "Ready to talk"
    : mode === "speaking" ? "Warren is speaking"
    : mode === "listening" ? "Listening…"
    : "Connected";

  if (compact) {
    return (
      <button
        onClick={active ? stop : start}
        disabled={connecting}
        data-testid="voice-mode-compact"
        title={active ? "End voice chat" : "Start voice chat"}
        className={[
          "w-10 h-10 rounded-full grid place-items-center transition-all shrink-0 border",
          active
            ? "bg-primary text-white border-primary animate-pulse"
            : "bg-white text-primary border-primary/40 hover:bg-primary hover:text-white hover:border-primary shadow-sm",
        ].join(" ")}
      >
        {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : active ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <div className="card-elevated p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="voice-mode-panel">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={[
          "shrink-0 w-14 h-14 rounded-full grid place-items-center transition-all",
          active
            ? "bg-gradient-to-br from-primary to-sky-400 text-white shadow-lg"
            : "bg-primary/10 text-primary",
          active && (mode === "speaking" || mode === "listening") ? "animate-pulse" : "",
        ].join(" ")}>
          {connecting ? <Loader2 className="w-6 h-6 animate-spin" />
            : active ? <Radio className="w-6 h-6" />
            : <Mic className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-xl">Talk to Warren</p>
          <p className="text-sm text-muted-foreground" data-testid="voice-mode-status">
            {statusLabel}
            {active && remaining != null && (
              <span className="ml-2 text-[11px]">· {Math.floor(remaining / 60)} min left today</span>
            )}
          </p>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
      </div>
      {active ? (
        <button onClick={stop} data-testid="voice-mode-stop"
          className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full bg-shell-side text-white px-5 py-2.5 text-sm font-semibold hover:bg-shell-side/90 transition shrink-0">
          <MicOff className="w-4 h-4" /> End
        </button>
      ) : (
        <button onClick={start} disabled={connecting} data-testid="voice-mode-start"
          className="w-full sm:w-auto justify-center btn-primary text-sm disabled:opacity-60 shrink-0">
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
          {connecting ? "Connecting…" : "Talk to Warren"}
        </button>
      )}
    </div>
  );
}
